import React, { useState } from "react";
import { Loader2, Save, ShieldCheck, FileDown, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";

const VERDICT_STYLE = {
    "Approved": "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    "Approved with observations": "bg-amber-500/15 text-amber-300 border-amber-500/40",
    "Remediation required": "bg-orange-500/15 text-orange-300 border-orange-500/40",
    "Rejected": "bg-rose-500/15 text-rose-300 border-rose-500/40",
    "Unable to determine without SAP validation": "bg-slate-500/15 text-slate-300 border-slate-500/40",
};

const TEXT_FIELDS = [
    ["object_name", "Object Name"], ["package", "Package"], ["transport", "Transport"],
    ["functional_module", "Functional Module"], ["business_process", "Business Process"],
    ["tcode", "Transaction Code"], ["fiori_app_id", "Fiori App ID"], ["odata_service", "OData Service"],
    ["calling_system", "Calling System"], ["target_system", "Target System"],
    ["expected_roles", "Expected Business Roles"], ["sensitive_data_category", "Sensitive Data Category"],
    ["required_approver", "Required Approver"],
];

export function RicefwClearancePanel({ session, reference, onUpdated }) {
    const [meta, setMeta] = useState(session.metadata || {});
    const [saving, setSaving] = useState(false);
    const [importing, setImporting] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const clr = session.clearance || {};
    const verdict = clr.verdict || "Unable to determine without SAP validation";

    const set = (k, v) => setMeta((m) => ({ ...m, [k]: v }));

    const saveMeta = async () => {
        setSaving(true);
        try {
            const { data } = await api.put(`/code-review/sessions/${session.id}/metadata`, meta);
            toast.success("RICEFW metadata saved — clearance recomputed");
            onUpdated?.(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const importAtc = async (file) => {
        if (!file) return;
        setImporting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/code-review/sessions/${session.id}/atc-import`, fd);
            toast.success(`Imported ${data.atc.total} official ATC findings`);
            onUpdated?.(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "ATC import failed");
        } finally { setImporting(false); }
    };

    const downloadReport = async () => {
        setDownloading(true);
        try {
            const res = await api.get(`/code-review/sessions/${session.id}/report`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `clearance_${(meta.object_name || "review").replace(/[^a-z0-9_-]+/gi, "_")}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Clearance report exported");
        } catch (e) {
            toast.error("Report export failed");
        } finally { setDownloading(false); }
    };

    return (
        <div className="panel-elevated rounded-[4px] p-4 space-y-4">
            {/* Verdict banner */}
            <div className={`rounded-[4px] border p-3 ${VERDICT_STYLE[verdict] || VERDICT_STYLE["Unable to determine without SAP validation"]}`}>
                <div className="flex items-center gap-2">
                    <ShieldCheck size={16} />
                    <span className="label-eyebrow !text-current opacity-80">Clearance Recommendation</span>
                </div>
                <div data-testid={TID.crVerdictBadge} className="text-lg font-display mt-1">{verdict}</div>
                <p className="text-[11px] mt-1 opacity-90">{clr.rationale}</p>
                <div className="flex flex-wrap gap-3 mt-2 text-[10px] font-mono">
                    <span>Open critical: <b>{clr.open_critical ?? 0}</b></span>
                    <span>Open high: <b>{clr.open_high ?? 0}</b></span>
                    <span>Accepted: <b>{clr.accepted_risks ?? 0}</b></span>
                    <span>Fixed: <b>{clr.fixed ?? 0}</b></span>
                    <span>Needs SAP validation: <b>{clr.requires_validation ?? 0}</b></span>
                </div>
                {clr.auto_approve_blocked && (
                    <p className="text-[10px] font-mono mt-1 opacity-80">Auto-approval blocked — unresolved critical/high findings present.</p>
                )}
            </div>

            {/* RICEFW metadata */}
            <div>
                <div className="label-eyebrow mb-2">RICEFW Object Metadata</div>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className="text-[9px] font-mono uppercase tracking-wider text-slate-500 block mb-0.5">Object Type</label>
                        <select data-testid={TID.crMetaField("object_type")} value={meta.object_type || ""} onChange={(e) => set("object_type", e.target.value)} className="field w-full">
                            <option value="">Select…</option>
                            {(reference.object_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-[9px] font-mono uppercase tracking-wider text-slate-500 block mb-0.5">Criticality</label>
                        <select data-testid={TID.crMetaField("criticality")} value={meta.criticality || "Medium"} onChange={(e) => set("criticality", e.target.value)} className="field w-full">
                            {(reference.criticality_levels || []).map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-[9px] font-mono uppercase tracking-wider text-slate-500 block mb-0.5">Data Classification</label>
                        <select data-testid={TID.crMetaField("data_classification")} value={meta.data_classification || ""} onChange={(e) => set("data_classification", e.target.value)} className="field w-full">
                            <option value="">Select…</option>
                            {(reference.data_classifications || []).map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-[9px] font-mono uppercase tracking-wider text-slate-500 block mb-0.5">Interface Direction</label>
                        <select data-testid={TID.crMetaField("interface_direction")} value={meta.interface_direction || ""} onChange={(e) => set("interface_direction", e.target.value)} className="field w-full">
                            <option value="">Select…</option>
                            {(reference.interface_directions || []).map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                    {TEXT_FIELDS.map(([k, label]) => (
                        <div key={k}>
                            <label className="text-[9px] font-mono uppercase tracking-wider text-slate-500 block mb-0.5">{label}</label>
                            <input data-testid={TID.crMetaField(k)} value={meta[k] || ""} onChange={(e) => set(k, e.target.value)} className="field w-full font-mono" />
                        </div>
                    ))}
                </div>
                <button data-testid={TID.crMetaSaveBtn} onClick={saveMeta} disabled={saving} className="btn-primary mt-3 inline-flex items-center gap-1.5 disabled:opacity-50">
                    {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save Metadata & Recompute
                </button>
            </div>

            {/* ATC import + report */}
            <div className="pt-3 border-t border-white/10 space-y-2">
                <div className="label-eyebrow">Official SAP ATC Export &amp; Clearance Report</div>
                {session.atc && (
                    <div className="space-y-1" data-testid={TID.crAtcSummary}>
                        <p className="text-[10px] font-mono text-slate-400">
                            Imported {session.atc.total} ATC rows from {session.atc.filename}
                            {session.atc.duplicate_rows ? ` · ${session.atc.unique_total} distinct (${session.atc.duplicate_rows} repeated rows collapsed)` : ""}
                            {" · "}{session.atc.security_relevant_count} security-relevant
                            {session.atc.exempted_count ? ` · ${session.atc.exempted_count} exempted in SAP` : ""}
                        </p>
                        {session.atc.correlation && (
                            <p className="text-[10px] font-mono text-emerald-300/90">
                                {session.atc.correlation.note}
                            </p>
                        )}
                        {session.atc.rows_truncated && (
                            <p className="text-[9px] font-mono text-amber-300/80">
                                Row detail capped at 500 for storage — all counts above cover the full export.
                            </p>
                        )}
                    </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                    <label className="btn-ghost inline-flex items-center gap-1.5 cursor-pointer">
                        {importing ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Import ATC (.xlsx/.csv)
                        <input
                            data-testid={TID.crAtcImportInput}
                            type="file" accept=".xlsx,.csv" className="hidden"
                            onChange={(e) => { importAtc(e.target.files?.[0]); e.target.value = ""; }}
                        />
                    </label>
                    <button data-testid={TID.crReportBtn} onClick={downloadReport} disabled={downloading} className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-50">
                        {downloading ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} Clearance Report PDF
                    </button>
                </div>
                <p className="text-[9px] font-mono text-slate-400">
                    Imported ATC results are official SAP findings, kept distinct from the Engineering Engine's ATC-equivalent static rules.
                </p>
            </div>
        </div>
    );
}
